import { useEffect, useState } from 'react';
import { FileQuestion } from 'lucide-react';
import { Permissao, SituacaoVinculo, type ArquivoEnviadoDto, type ProcessoCartaoDto } from '@nexital/shared';
import { useEnvio, useQuadro } from '../../application/dependencias';
import { useConfirmar } from '../design-system/Confirmacao';

/** Lista vazia fixa: o seletor do store não pode devolver uma lista nova a cada leitura (laço infinito no React). */
const NENHUM: never[] = [];

/** Tipo do dado arrastado da bandeja de arquivos até um cartão (arrastar e soltar do HTML). */
export const ARRASTE_ARQUIVO = 'application/x-nexital-arquivo';
const INTERVALO_MS = 15_000;

type PropsArquivo = { arquivo: ArquivoEnviadoDto; processos: ProcessoCartaoDto[]; pode: boolean };

function ArquivoDaBandeja({ arquivo, processos, pode }: PropsArquivo) {
  const agir = useEnvio((s) => s.agir);
  const confirmar = useConfirmar();
  const descartar = async () => {
    if (await confirmar({ titulo: 'Descartar o arquivo?', mensagem: `${arquivo.nome} sai da bandeja e não vai para nenhum processo.`, rotulo: 'Descartar' })) void agir(arquivo.uuid, 'descartar');
  };
  const sugerido = arquivo.situacao === SituacaoVinculo.Sugerido ? arquivo.sugerido : null;
  const vincular = (uuid: string) => {
    const p = processos.find((x) => x.uuid === uuid);
    if (p) void agir(arquivo.uuid, 'vincular', { uuid: p.uuid, numero: p.numero });
  };
  return (
    <li
      className="arquivos-sem-processo__item"
      draggable={pode}
      onDragStart={(e) => {
        e.dataTransfer.setData(ARRASTE_ARQUIVO, arquivo.uuid);
        e.dataTransfer.effectAllowed = 'link';
      }}
    >
      <strong title={arquivo.nome}>{arquivo.nome}</strong>
      {arquivo.origemZip && <small>de {arquivo.origemZip}</small>}
      <span className="arquivos-sem-processo__motivo">{arquivo.motivo}</span>
      {pode && (
        <div className="arquivos-sem-processo__acoes">
          {sugerido && (
            <button type="button" className="botao botao--primario" onClick={() => vincular(sugerido.uuid)}>
              Anexar a {sugerido.numero}
            </button>
          )}
          <select aria-label={`Vincular ${arquivo.nome} a um processo`} value="" onChange={(e) => e.target.value && vincular(e.target.value)}>
            <option value="">Vincular a…</option>
            {processos.map((p) => (
              <option key={p.uuid} value={p.uuid}>
                {p.numero} — {p.objeto.slice(0, 40)}
              </option>
            ))}
          </select>
          <button type="button" className="botao botao--secundario" onClick={() => void agir(arquivo.uuid, 'nova-licitacao')}>
            Nova licitação
          </button>
          <button type="button" className="botao botao--secundario" onClick={() => void descartar()}>
            Descartar
          </button>
        </div>
      )}
    </li>
  );
}

/**
 * Bandeja "Arquivos sem processo" (ADR-0020), acima da de Sem estado: arquivos que o Nexital não conseguiu ligar a um
 * processo com certeza. Cada um pode ser arrastado até um cartão, anexado ao processo sugerido, vinculado pela lista
 * (alternativa ao arraste pelo teclado), virar uma licitação nova ou ser descartado.
 */
export function ArquivosSemProcesso() {
  const bandeja = useEnvio((s) => s.bandeja);
  const carregar = useEnvio((s) => s.carregarBandeja);
  const processos = useQuadro((s) => s.dados?.processos ?? NENHUM);
  const pode = useQuadro((s) => s.dados?.permissoes.includes(Permissao.EnviarDocumentos) ?? false);
  const [aberta, definirAberta] = useState(false);
  useEffect(() => {
    void carregar();
    const relogio = setInterval(() => void carregar(), INTERVALO_MS);
    return () => clearInterval(relogio);
  }, [carregar]);

  return (
    <>
      {bandeja.length > 0 && (
        <section className={`sem-estado arquivos-sem-processo${aberta ? ' sem-estado--aberta arquivos-sem-processo--aberta' : ''}`} aria-label="Arquivos sem processo">
          <button type="button" className="sem-estado__botao" onClick={() => definirAberta(!aberta)} aria-expanded={aberta}>
            <FileQuestion size={18} aria-hidden />
            <span>Arquivos sem processo</span>
            <span className="bandeja__total" aria-label={`${bandeja.length} arquivo(s)`}>{bandeja.length}</span>
          </button>
          {aberta && (
            <>
              <ul className="sem-estado__lista">
                {bandeja.map((a) => (
                  <ArquivoDaBandeja key={a.uuid} arquivo={a} processos={processos} pode={pode} />
                ))}
              </ul>
              <p className="sem-estado__vazio">Arraste um arquivo daqui até o cartão do processo.</p>
            </>
          )}
        </section>
      )}
    </>
  );
}
